import { useState } from 'react';
import {
  formatMoney,
  formatQuantity,
  hoy,
  newId,
  parseQuantity,
  type Centavos,
  type Milli,
} from '@abastece/shared';
import type { Producto } from '@abastece/core';
import { existenciaId } from '@abastece/core';
import { db } from '../db/dexie.js';
import { emitirYVer } from '../lib/emitir.js';
import { volver } from '../lib/volver.js';
import { usePuede } from '../lib/sesion.js';
import { BuscadorProducto } from '../ui/BuscadorProducto.js';
import { FormularioProducto } from '../ui/FormularioProducto.js';
import { Modal } from '../ui/Modal.js';
import { Pagina } from '../ui/Pagina.js';
import { Ico } from '../ui/Iconos.js';
import { useT } from '../i18n/index.js';
import { etiquetaUnidad } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';
import { Miniatura } from '../ui/Miniatura.js';

interface Fila {
  key: string;
  producto: Producto;
  contado: string;
  /**
   * What the books said here when the number was written, and when: the count applies the
   * difference, so what sells while the rest is being counted is not put back on the shelf.
   */
  sistema?: number;
  contadoEn?: string;
}

/**
 * Conteo físico, blind by default.
 *
 * The system quantity stays hidden while counting, for the same reason the corte does: a count
 * taken while looking at the answer measures nothing. The differences appear once it is
 * submitted — and that is the number worth looking at.
 *
 * A product taken off sale is counted too (the owner, 2026-10-18): what is still on the shelf is
 * stock whether it is sold or not, and «Inventario» lists it. The search finds it, marked
 * «Desactivado», and a scan when nothing on sale has its code.
 */
export function Conteo() {
  const t = useT();
  const nombre = useNombreProducto();

  // The shop's, the one place there is (the owner, 2026-10-05).
  const ubicacion = 'tienda';
  const [filas, setFilas] = useState<Fila[]>([]);
  const [resultado, setResultado] = useState<
    { producto: Producto; contado: number; sistema: number; diferencia: number }[] | null
  >(null);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Something on the shelf the catalogue has not got, being made a product over the count, as in
   * «Mercadería comprada» (the owner, 2026-10-04): its code, or the name it was looked for under.
   */
  const [nuevo, setNuevo] = useState<{ codigo?: string; nombre?: string } | null>(null);
  const puedeCrear = usePuede('editar_productos');

  function agregar(producto: Producto) {
    setFilas((f) =>
      f.some((x) => x.producto.id === producto.id)
        ? f
        : [...f, { key: newId(), producto, contado: '' }],
    );
  }

  /** The product just made in the sheet, onto the count, to be counted like the rest. */
  async function creado({ productoId }: { productoId: string; unidadVentaId: string }) {
    const producto = await db.productos.get(productoId);
    if (producto) agregar(producto);
    setNuevo(null);
  }

  /** Notes what the books say for these rows right now: the moment their number was written. */
  async function anotar(keys: readonly string[], lugar: string) {
    const contadoEn = new Date().toISOString();
    const libros = new Map(
      await Promise.all(
        filas
          .filter((f) => keys.includes(f.key))
          .map(async (f) => [f.key, (await db.existencias.get(existenciaId(f.producto.id, lugar)))?.cantidad ?? 0] as const),
      ),
    );
    setFilas((xs) => xs.map((x) => (libros.has(x.key) ? { ...x, sistema: libros.get(x.key)!, contadoEn } : x)));
  }

  /** What a row's box reads as: null while empty, `mal` when it is not a count (negative, or not a number). */
  const leido = (texto: string): Milli | 'mal' | null => {
    if (texto.trim() === '') return null;
    const q = parseQuantity(texto);
    return q === null || q < 0 ? 'mal' : q;
  };
  const listas = filas.filter((f) => typeof leido(f.contado) === 'number' && f.contadoEn !== undefined);
  /**
   * Typed and not a count: «-3» passed, the books refused the whole count, and the screen stayed on
   * «Guardando…» for good with every row counted in it (2026-10-13). Said, and nothing is saved until
   * it is put right.
   */
  const malas = filas.filter((f) => leido(f.contado) === 'mal');

  async function guardar() {
    if (listas.length === 0 || malas.length > 0 || guardando) return;
    setGuardando(true);
    setError(null);
    try {
      const { rechazo } = await emitirYVer('inventario.conteo', {
        conteoId: newId(),
        ubicacion,
        fecha: hoy(),
        lineas: listas.map((f) => ({
          productoId: f.producto.id,
          cantidadContada: parseQuantity(f.contado)!,
          cantidadSistema: f.sistema!,
          contadoEn: f.contadoEn!,
        })),
      });
      if (rechazo) {
        setError(rechazo);
        return;
      }
      setResultado(
        listas.map((f) => {
          const contado = parseQuantity(f.contado)!;
          return { producto: f.producto, contado, sistema: f.sistema!, diferencia: contado - f.sistema! };
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : t('entrega.noSeGuardo'));
    } finally {
      setGuardando(false);
    }
  }

  if (resultado) {
    const cuadran = resultado.filter((r) => r.diferencia === 0).length;
    return (
      <Pagina titulo={t('conteo.resultado')} volverA="/inventario">
        <p className="mb-4 text-sm text-slate-400">
          {t('conteo.cuadraron', { n: cuadran, total: resultado.length })}
        </p>
        <ul className="flex flex-col gap-2">
          {resultado.map((r) => (
            <li key={r.producto.id} className="tarjeta flex items-center gap-3 py-3 pl-3">
              <Miniatura producto={r.producto} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{nombre(r.producto)}</p>
                <p className="tabular mt-0.5 text-xs text-slate-500">
                  {t('conteo.detalle', {
                    sistema: formatQuantity(r.sistema as never),
                    contado: formatQuantity(r.contado as never),
                  })}
                </p>
              </div>
              <span
                className={`tabular shrink-0 font-semibold ${
                  r.diferencia === 0
                    ? 'text-emerald-400'
                    : r.diferencia < 0
                      ? 'text-rose-400'
                      : 'text-amber-400'
                }`}
              >
                {r.diferencia === 0
                  ? t('conteo.cuadro')
                  : `${r.diferencia > 0 ? '+' : ''}${formatQuantity(r.diferencia as never)}`}
              </span>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="btn-primario mt-5 w-full"
          onClick={() => void volver({ to: '/inventario' })}
        >
          <Ico icono="listo" />
          {t('caja.listo')}
        </button>
      </Pagina>
    );
  }

  return (
    <Pagina
      titulo={t('conteo.titulo')}
      subtitulo={t('conteo.conCantidad', { n: listas.length })}
      volverA="/inventario"
    >
      <section className="tarjeta mb-4 flex flex-col gap-4">
        <div data-guia="conteo-agregar">
          {/*
            A scan with the cursor in a row's count adds its product, and leaves that count as it was.
            What the catalogue has not got is made a product from here, by a name nothing is called or
            a code nobody has, by whoever may edit products.
          */}
          <BuscadorProducto
            onElegir={agregar}
            etiqueta={t('conteo.agregarProducto')}
            autoFocus
            lectorUsb
            preguntarEnHoja
            inactivos
            onNuevo={puedeCrear ? (nombre) => setNuevo({ nombre }) : undefined}
            escaner={{
              desconocido: puedeCrear
                ? (codigo, cerrar) => (
                    <button
                      type="button"
                      className="btn-secundario"
                      onClick={() => {
                        cerrar();
                        setNuevo({ codigo });
                      }}
                    >
                      <Ico icono="nuevo" />
                      {t('productos.crearConCodigo')}
                    </button>
                  )
                : undefined,
            }}
          />
        </div>
        <p className="text-xs text-slate-500">{t('conteo.explicacion')}</p>
      </section>

      {filas.length > 0 && (
        <ul className="mb-4 flex flex-col gap-2" data-guia="conteo-filas">
          {filas.map((f) => (
            <li key={f.key} className="tarjeta flex items-center gap-3 py-3 pl-3">
              <Miniatura producto={f.producto} tamano="sm" className="max-[359px]:hidden" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{nombre(f.producto)}</p>
                <p className="text-xs text-slate-500">
                  {etiquetaUnidad(f.producto.unidadBase, f.producto.unidadBase)}
                  {!f.producto.activo && ` · ${t('buscador.desactivado')}`}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <input
                  className={`campo tabular h-11 w-28 min-h-0 px-3 text-right ${leido(f.contado) === 'mal' ? 'border-rose-500' : ''}`}
                  inputMode="decimal"
                  placeholder="0"
                  aria-invalid={leido(f.contado) === 'mal'}
                  aria-label={nombre(f.producto)}
                  value={f.contado}
                  onChange={(e) => {
                    setFilas((xs) =>
                      xs.map((x) => (x.key === f.key ? { ...x, contado: e.target.value } : x)),
                    );
                    void anotar([f.key], ubicacion);
                  }}
                />
                <button
                  type="button"
                  className="btn-texto gap-1 text-xs text-slate-500"
                  onClick={() => setFilas((xs) => xs.filter((x) => x.key !== f.key))}
                >
                  <Ico icono="quitar" className="size-3.5" />
                  {t('comun.quitar')}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {malas.length > 0 && (
        <p role="alert" className="mb-3 text-sm text-rose-400" data-conteo-malas>
          {t('conteo.malas', { lista: malas.map((f) => nombre(f.producto)).join(', ') })}
        </p>
      )}
      {error && (
        <p role="alert" className="mb-3 text-sm text-rose-400">
          {error}
        </p>
      )}
      <button
        type="button"
        className="btn-primario w-full"
        data-guia="conteo-registrar"
        disabled={listas.length === 0 || malas.length > 0 || guardando}
        onClick={() => void guardar()}
      >
        <Ico icono="contar" />
        {t(guardando ? 'comun.guardando' : 'conteo.registrar')}
      </button>
      {/* Made here, the count still as it was behind the sheet, and once saved it is a row of it. */}
      {nuevo && (
        <Modal titulo={t('producto.nuevo')} onCerrar={() => setNuevo(null)}>
          <p className="mb-4 text-sm text-slate-400">{t('conteo.productoNuevoAyuda')}</p>
          <FormularioProducto enHoja {...nuevo} onCreado={(c) => void creado(c)} />
        </Modal>
      )}
    </Pagina>
  );
}

export type { Centavos };
